/**
 * print/PolaroidStripArtifact — the canonical first artifact.
 *
 * A vertical three-frame strip in walnut/cream atmospheric
 * register. The composition:
 *
 *   ┌─────────────────────┐  ← bleed
 *   │   ✦ event title     │  ← in safe zone
 *   │   date · location   │
 *   │  ┌───────────────┐  │  ← square photo 1
 *   │  │      img      │  │
 *   │  └───────────────┘  │
 *   │  ┌───────────────┐  │  ← square photo 2
 *   │  │      img      │  │
 *   │  └───────────────┘  │
 *   │  ┌───────────────┐  │  ← square photo 3
 *   │  │      img      │  │
 *   │  └───────────────┘  │
 *   │      ✦ EventDrop    │  ← cream serif baseline
 *   └─────────────────────┘
 *
 * Rules (lock these — they belong to the artifact, not the studio):
 *   • Mode 1 palette: walnut #2A1E12, cream #F4ECDE, restrained gold.
 *   • No buttons, no pills, no gradients-of-the-app, no SaaS residue.
 *   • Type set in the canonical serif; only restrained sizes.
 *   • Photos always square-cropped centre to land print-deterministic.
 *   • Drawn entirely in mm space — pixel decisions live in the canvas.
 *
 * The same composition is mirrored on the backend (Pillow) so the
 * preview and the export agree on geometry.
 */
import React from 'react';

const SERIF = 'EB Garamond, "Cormorant Garamond", "Playfair Display", Georgia, serif';

// Walnut palette tokens — these are the artifact's, NOT the app's.
const PAPER  = '#F4ECDE'; // parchment
const INK    = '#2A1E12'; // walnut
const INK_MUTED = '#5A4A38';
const GOLD   = '#B89762';

const PolaroidStripArtifact = ({
  geometry,         // { trimRect, safeRect, format } supplied by PrintCanvas
  event,            // { event_name, event_date, slug }
  photos = [],      // up to 3 photo URLs (already absolute)
}) => {
  if (!geometry) return null;
  const { safeRect } = geometry;

  // We work entirely in mm. The safe rectangle defines the working
  // area for everything critical.
  const colX = safeRect.x;
  const colW = safeRect.w;
  const colY = safeRect.y;
  const colH = safeRect.h;

  // Vertical rhythm — title block / 3 photo squares / baseline.
  const TITLE_BLOCK_MM = 14;
  const BASELINE_MM    = 9;
  const PHOTO_GAP_MM   = 2.6;
  const photosTotalH = colH - TITLE_BLOCK_MM - BASELINE_MM - 2 * PHOTO_GAP_MM;
  const photoSide = Math.min(colW, photosTotalH / 3);
  const photoX = colX + (colW - photoSide) / 2;
  const firstPhotoY = colY + TITLE_BLOCK_MM;

  // Date formatter — the artifact is print-bound so we format
  // explicitly here rather than relying on locale defaults.
  const dateLine = (() => {
    if (!event?.event_date) return '';
    try {
      const d = new Date(`${event.event_date}T00:00:00`);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return ''; }
  })();

  // We render exactly 3 slots; missing photos show a calm cream
  // square with a small ✦ mark — never an error state, never an
  // empty UI placeholder.
  const slots = [0, 1, 2].map((i) => photos[i] || null);

  return (
    <g data-testid="polaroid-strip-artifact">
      {/* Title — set in safe zone with restrained leading */}
      <text
        x={colX + colW / 2}
        y={colY + 4.5}
        textAnchor="middle"
        fontFamily={SERIF}
        fontSize={3.6}
        fontStyle="italic"
        fill={INK}
        data-testid="polaroid-title"
      >{event?.event_name || 'A preserved night'}</text>
      <text
        x={colX + colW / 2}
        y={colY + 9}
        textAnchor="middle"
        fontFamily={SERIF}
        fontSize={2.4}
        fill={INK_MUTED}
        letterSpacing="0.06"
        data-testid="polaroid-date"
      >{dateLine}</text>
      {/* Restrained gold glyph — single ✦, no other ornament */}
      <text
        x={colX + colW / 2}
        y={colY + 12.5}
        textAnchor="middle"
        fontFamily={SERIF}
        fontSize={3}
        fill={GOLD}
      >✦</text>

      {/* Three square photo wells */}
      {slots.map((src, i) => {
        const y = firstPhotoY + i * (photoSide + PHOTO_GAP_MM);
        return (
          <g key={i} data-testid={`polaroid-slot-${i}`}>
            {/* Cream backing — visible when no photo lands */}
            <rect
              x={photoX} y={y}
              width={photoSide} height={photoSide}
              fill="#EFE4D0"
            />
            {src ? (
              <image
                href={src}
                x={photoX} y={y}
                width={photoSide} height={photoSide}
                preserveAspectRatio="xMidYMid slice"
              />
            ) : (
              <text
                x={photoX + photoSide / 2}
                y={y + photoSide / 2 + 1}
                textAnchor="middle"
                fontFamily={SERIF}
                fontSize={4}
                fill={GOLD}
                opacity={0.55}
              >✦</text>
            )}
            {/* Hairline frame */}
            <rect
              x={photoX} y={y}
              width={photoSide} height={photoSide}
              fill="none"
              stroke={INK}
              strokeOpacity={0.18}
              strokeWidth={0.18}
            />
          </g>
        );
      })}

      {/* Baseline wordmark — italic serif, restrained */}
      <text
        x={colX + colW / 2}
        y={colY + colH - 1.5}
        textAnchor="middle"
        fontFamily={SERIF}
        fontSize={2.6}
        fontStyle="italic"
        fill={INK_MUTED}
        letterSpacing="0.18"
        data-testid="polaroid-wordmark"
      >EventDrop ✦</text>
    </g>
  );
};

export default PolaroidStripArtifact;

// Token export so the server-side renderer can import the same
// palette/typography decisions and stay in lockstep with preview.
export const POLAROID_TOKENS = {
  PAPER, INK, INK_MUTED, GOLD, SERIF,
};
